import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, input, OnInit, signal } from '@angular/core';
import { VisualEditor, VisualEditorComponent } from '../../VisualEditor.component';
import { isWhitespaceOnly } from 'src/app/shared/utils/is-whitespace-only';
import { AppContext } from 'src/app/app/context';
import { ListItemCategoryDto, ListUserDto, OrganisationService, UserDatasetService, UserRole, UserService } from 'src/app/services/api.services';
import { refreshLogToFormatedDetails, SettingsRefreshDetails } from './SettingsRefreshDetails';
import { getIconForKnownProduct, ProductIcon } from 'src/app/shared/utils/products';
import { LocalisationService } from 'src/app/services/localisation.service';
import { unitOfTime } from 'moment';
import moment from 'moment';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DialogRef } from '@angular/cdk/dialog';
import { finalize, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchAppError } from 'src/app/shared/utils';

@Component({
  selector: 'visual-settings-dialog',
  templateUrl: './VisualSettings.dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class VisualSettingsDialog implements OnInit {
  readonly limits = VisualEditor.limits;

  ngOnInit(): void {
    this.loadUsers();

    if (this.visualEditor.createdBy() == this.user().username)
      this.wasOwner.set(true);
  }

  loadUsers() {
    this.userService.getUsersForOrganisation().pipe(
      tap(users => {
        this.owners.set(users.sort((a, b) => a.username.localeCompare(b.username)));
      }),
      catchAppError(),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.loadingUsers.set(false);
      })
    ).subscribe();
  }

  readonly destroyRef = inject(DestroyRef);
  readonly dialogRef = inject(DialogRef);
  readonly visualEditor = inject<VisualEditorComponent>(MAT_DIALOG_DATA);
  readonly userDatasetService = inject(UserDatasetService);
  readonly localisationService = inject(LocalisationService);
  readonly appContext = inject(AppContext);
  readonly userService = inject(UserService);
  readonly products = computed(() => this.appContext.products());

  readonly itemCategories = computed(() => {
    return this.appContext.itemCategories().map((category) => {
      return <FormattedCategory>{
        ...category,
        icon: this.getItemCategoryIcon(category),
        formattedName: this.formatItemCategoryName(category)
      };
    });
  });

  readonly user = computed(() => this.appContext.user());

  readonly canChangeOwner = computed(() => {
    let isExisting = this.visualEditor.visualId() != 0;
    let canRename = (this.user().isSupportAdmin || this.visualEditor.createdBy() == this.user().username);

    return isExisting && (canRename || this.wasOwner());
  });

  readonly ownerDisabledStateEffect = effect(() => {
    if (this.busy()) {
      this.visualEditor.subtitle.control.disable({ onlySelf: true });
      this.visualEditor.description.control.disable({ onlySelf: true });
      this.visualEditor.categoryIds.control.disable({ onlySelf: true });
    }
    else {
      this.visualEditor.subtitle.control.enable({ onlySelf: true });
      this.visualEditor.description.control.enable({ onlySelf: true });
      this.visualEditor.categoryIds.control.enable({ onlySelf: true });
    }

    if (this.loadingUsers() || !this.canChangeOwner() || this.busy())
      this.visualEditor.createdBy.control.disable({onlySelf: true});
    else
      this.visualEditor.createdBy.control.enable({onlySelf: true});
  });

  readonly allowProfilingUserDataset = computed(() => this.appContext.features().extendedDatasets.allowProfiling && this.isUserDataset());
  readonly visual = computed(() => this.visualEditor.visual());
  readonly name = computed(() => this.visualEditor.visualName());

  get isUserDataset() { return this.visualEditor.isUserDataset; };
  get userDatasetDto() { return this.visualEditor.userDatasetDto; };
  get userDatasetState() { return this.visualEditor.userDatasetState; };
  get userDatasetRefreshLog() { return this.visualEditor.userDatasetRefreshLog; };

  readonly userDatasetNextPurgeOn = computed(() => this.userDatasetState().expirationDate);
  readonly userDatasetCacheDuration = computed(() => this.userDatasetState().cacheDuration);

  readonly userDatasetLoadFailed = computed(() => this.userDatasetState().status == VisualEditor.UserDatasetStatus.Failed);

  readonly refreshDetails = signal(false);
  //Were we the owner of this visual when this dialog was opend?
  readonly wasOwner = signal(false);

  readonly busy = input(false);

  readonly loadingUsers = signal(true);
  readonly owners = signal<ListUserDto[]>(null);

  getNameOrDefault = computed(() => isWhitespaceOnly(this.name()) ? '-- unnamed --' : this.name());

  setUserDatasetProfiling(profile: boolean) {
    this.userDatasetService.setProfiling(this.userDatasetDto().id, profile).pipe(
      tap( () => {
        this.visualEditor.userDatasetDto.update((prev) => {
          return {...prev, profile};
        });
      }),
      catchAppError(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  setRefreshDetails(value: boolean) {
    this.refreshDetails.set(value);
  }

  settingRefreshDetails = computed(() => {
    return refreshLogToFormatedDetails(this.userDatasetRefreshLog());
  });

  validateCacheDuration($event) {
    if ($event?.target?.value > 365) $event.target.value = 365;
    if ($event?.target?.value < 1) $event.target.value = 1;
  }

  private formatItemCategoryName(itemCategory: ListItemCategoryDto) {
    const product = this.products().find((p) => p.id === itemCategory.productId);
    if (!product) {
      return itemCategory.name;
    }

    return `${itemCategory.name} (${product.name})`;
  }

  private getItemCategoryIcon(itemCategory: ListItemCategoryDto) {
    const product = this.products().find((p) => p.id === itemCategory.productId);
    if (!product) return null;

    return getIconForKnownProduct(product.name);
  }

  setUserDatasetCacheDuration(duration: number) {
    this.visualEditor.userDatasetState.update((prev) => {
      const type = "d";
      if (duration > 365) duration = 365;
      if (duration < 1) duration = 1;

      const lastRefresh = moment(prev.lastRefreshed);
      const purgeDate = lastRefresh.add(duration, <unitOfTime.DurationConstructor>type);

      return {
        status: prev.status,
        lastRefreshed: prev.lastRefreshed,
        lastCached: prev.lastCached,
        cacheDuration: duration,
        expirationDate: purgeDate.toISOString()
      };
    });

    this.visualEditor.visualChanged.set(true);
  }

  close() {
    this.dialogRef.close();
  }
}


interface FormattedCategory extends ListItemCategoryDto {
  icon: ProductIcon,
  formattedName: string;
}
